import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../Shared/API/config';

export interface BehaviorType {
  id: string;
  displayName: string;
}

export interface BehaviorSeverity {
  id: string;
  displayName: string;
}

export interface Behavior {
  id?: number;
  displayName: string;
  description?: string;
  type: BehaviorType;
  severity: BehaviorSeverity;
  points: number;
}

export interface BehaviorCreateRequest {
  displayName: string;
  description?: string;
  type: string; 
  severity: string;
  points: number;
}

export interface BehaviorResponse {
  data: Behavior[];
  meta: {
    types: {
      [key: string]: string;
    };
    severities: {
      [key: string]: string;
    };
  };
}

export interface BehaviorLog {
  id?: number;
  behaviorId: number;
  studentIds: number[];
  comment?: string;
  loggedAt?: string;
  loggedBy?: string;
}

export interface StudentRanking {
  student_id: number;
  display_name: string;
  student_number: string;
  class_name: string | null;
  positive_points: number;
  negative_points: number;
  net_points: number;
  rank: number;
}

export interface BehaviorLogRequest {
  comment?: string;
  studentIds: number[];
}

export interface BehaviorQueryParams {
  search?: string;
  type?: string;
  severity?: string;
}

export interface StudentRankingParams {
  period?: string;
  class_id?: number;
  limit?: number;
}

export const BehaviorApi = createApi({
  reducerPath: 'BehaviorApi',
  baseQuery,
  tagTypes: ['Behavior', 'BehaviorLog'],
  endpoints: builder => ({
    getBehaviors: builder.query<BehaviorResponse, BehaviorQueryParams>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();

        if (params.search) searchParams.append('search', params.search);
        if (params.type) searchParams.append('type', params.type);
        if (params.severity) searchParams.append('severity', params.severity);

        const queryString = searchParams.toString();
        return `/academics/behaviors${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Behavior'],
    }),

    createBehavior: builder.mutation<any, BehaviorCreateRequest>({
      query: newBehavior => ({
        url: '/academics/behaviors',
        method: 'POST',
        body: newBehavior,
      }),
      invalidatesTags: ['Behavior'],
    }),

    updateBehavior: builder.mutation<
      any,
      { id: number; updates: Partial<BehaviorCreateRequest> }
    >({
      query: ({ id, updates }) => ({
        url: `/academics/behaviors/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['Behavior'],
    }),

    deleteBehavior: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/academics/behaviors/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Behavior'],
    }),

    logBehavior: builder.mutation<
      any,
      { behaviorId: number; data: BehaviorLogRequest }
    >({
      query: ({ behaviorId, data }) => ({
        url: `/academics/behaviors/${behaviorId}/log`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['BehaviorLog'],
    }),

    getStudentBehaviorLogs: builder.query<any, { studentId: number }>({
      query: ({ studentId }) => `/academics/students/${studentId}/behaviors`,
      providesTags: ['BehaviorLog'],
    }),

    getStudentBehaviorSummary: builder.query<any, { studentId: string }>({
      query: ({ studentId }) =>
        `/academics/students/${studentId}/behaviors/summary`,
      providesTags: ['BehaviorLog'],
    }),

    getStudentRankings: builder.query<StudentRanking[], StudentRankingParams>({
      query: params => {
        const searchParams = new URLSearchParams();
        if (params.period) searchParams.append('period', params.period);
        if (params.class_id)
          searchParams.append('class_id', params.class_id.toString());
        if (params.limit) searchParams.append('limit', params.limit.toString());

        const queryString = searchParams.toString();
        return `/academics/behavior/rankings${
          queryString ? `?${queryString}` : ''
        }`;
      },
      providesTags: ['BehaviorLog'],
    }),
  }),
});

export const {
  useGetBehaviorsQuery,
  useCreateBehaviorMutation,
  useUpdateBehaviorMutation,
  useDeleteBehaviorMutation,
  useLogBehaviorMutation,
  useGetStudentBehaviorLogsQuery,
  useGetStudentBehaviorSummaryQuery,
  useGetStudentRankingsQuery,
} = BehaviorApi;
